import { defineStore } from 'pinia'
import type PlayerPlanet from '@/types/PlayerPlanet'
import { useAssetStore } from '@/stores/assetStore.ts'
import type Building from '@/types/Building.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { logger } from '@/models/logger';
import { useShipDesignStore } from '@/stores/shipDesignStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'

export const usePlanetStore = defineStore('planetStore', {
  state: () => ({
    planets: new Map<number, PlayerPlanet>(),
    tempPlanet: null,
    isBatching: false, // To track batching status
    planetStatsTab: 'food' as string, // To track the active tab in the planet stats section
    planetStatsTabNoData: false as boolean, // Pass along if the active tab has no data,
    planetProductionDisplayMode: '' as string, // Used for toggling the build mode in planet production screen
    planetProductionMenuType: 'all' as string // Used filtering the menu in planet production screen
  }),
  getters: {
    findPlanetById:
      (state) =>
      (id: number): PlayerPlanet | undefined => {
        return state.planets.get(id)
      },
    allPlanetsArray: (state) => {
      return Array.from(state.planets.values())
    }
  },
  actions: {
    getNumberOfShipsInProduction(shipDesignId: number): number {
      let count = 0
      for (const planet of this.planets.values()) {
        count += planet.productionShipDesignId === shipDesignId ? 1 : 0
      }
      return count
    },
    getTotalCreditOutput() {
      let credits = 0
      for (const planet of this.planets.values()) {
        credits += planet.creditsPerTurn
      }
      return credits
    },
    getRawTotalScienceOutput() {
      let science = 0
      for (const planet of this.planets.values()) {
        science += planet.sciencePerTurn
      }
      return science
    },
    getTotalScienceOutput() {
      const science = this.getRawTotalScienceOutput()

      // adjust of science that goes to the alliance
      const gameStore = useGameStore()

      // Only apply alliance science allocation if player is in an alliance and alliance is researching a prototype
      if (gameStore.player?.allianceId) {
        const allianceStore = useAllianceStore()
        const alliance = allianceStore.getAlliance(gameStore.player.allianceId)

        if (alliance?.currentTechId) {
          const alliancePercentage = gameStore.player.allianceSciencePointsAllocationPercentage ?? 0
          const personalScience = (science * (100 - alliancePercentage)) / 100
          return Math.ceil(personalScience)
        }
      }

      return science
    },
    getTotalStorageCapacity() {
      let storage = 0
      for (const planet of this.planets.values()) {
        storage += planet.oreStorageCapacity
      }
      return storage
    },
    countPlanetsTerraForming() {
      const gameStore = useGameStore()
      let count = 0
      if (gameStore.player) {
        for (const planet of this.planets.values()) {
          if (Number(planet.type) != gameStore.player?.homeWorldPlanetType) {
            // Check if the planet has a terraformer and terraforming is enabled
            if (planet.buildings.includes('terraforming_command_center') && planet.terraformingEnabled) {
              count++
            }
          }
        }
      }
      return count
    },
    countPlanetsBuildingDysonSwarm() {
      const gameStore = useGameStore()
      let count = 0
      if (gameStore.player) {
        for (const planet of this.planets.values()) {
          // Check if the planet has a dyson swarm construction facility
          if (planet.buildings.includes('dyson_sphere_construction_facility')) {
            if (planet.dysonSwarmsBuilt < 5) {
              count++
            }
          }
        }
      }
      return count
    },
    countPlanetsWithRingworldConversion() {
      const gameStore = useGameStore()
      let count = 0
      for (const planet of this.planets.values()) {
        if (planet.ringworldConversionEnabled && 
            planet.ringworldConversionProgress < (gameStore.ringworldOrePerPlanet || 3)) {
          count++
        }
      }
      return count
    },
    getActiveDefenceBuildings(planetId: number) {
      const assetStore = useAssetStore()

      const planet = this.planets.get(planetId)
      if (!planet) {
        return []
      }
      const activeDefenceBuildings: Building[] = []
      planet.buildings.forEach((buildingCode) => {
        const building = assetStore.findBuildingByCode(buildingCode)
        building.effects.forEach((effect: any) => {
          if (effect.type === 'defence_attack_ships') {
            activeDefenceBuildings.push(building)
          }
        })
      })

      return activeDefenceBuildings
    },
    getPassiveDefenceBuildings(planetId: number) {
      const assetStore = useAssetStore()

      const planet = this.planets.get(planetId)
      if (!planet) {
        return []
      }
      const passiveDefenceBuildings: Building[] = []
      planet.buildings.forEach((buildingCode) => {
        const building = assetStore.findBuildingByCode(buildingCode)
        building.effects.forEach((effect: any) => {
          if (effect.type === 'defence_shields') {
            passiveDefenceBuildings.push(building)
          }
        })
      })

      return passiveDefenceBuildings
    },
    countBuildingsOfType(buildingCode: string) {
      let count = 0
      for (const planet of this.planets.values()) {
        for (const building of planet.buildings) {
          if (building == buildingCode) {
            count++
          }
        }
      }
      return count
    },
    hasBuilding(planetId: number, requiredBuildingCode: string) {
      const planet = this.planets.get(planetId)
      if (!planet) {
        return false
      }
      for (const buildingCode of planet.buildings) {
        if (buildingCode === requiredBuildingCode) {
          return true // Found the required building, so return true immediately
        }
      }
      return false
    },
    updatePlanetById(id: number, data: PlayerPlanet) {
      // Allow receiving of partial updates
      if (this.planets.has(id)) {
        const item = this.planets.get(id)
        // Manually merge changes
        for (const key in data) {
          // @ts-ignore
          if (data[key] !== undefined) {
            // @ts-ignore
            item[key] = data[key]
          }
        }
        // @ts-ignore
        data = item
      }

      const gameStore = useGameStore()
      const assetStore = useAssetStore()
      const shipDesignStore = useShipDesignStore()

      const sizeCompatible = data.size === gameStore.player?.homeWorldPlanetSize
      const sizeInCompatible = data.size === gameStore.player?.homeWorldOppositePlanetSize
      const typeCompatible = Number(data.type) === gameStore.player?.homeWorldPlanetType
      const typeInCompatible = Number(data.type) === gameStore.player?.homeWorldOppositePlanetType

      let compatibility = 'C'
      if (Number(data.type) === 7) {
        compatibility = 'F'
      } else if (Number(data.type) === 8) {
        // RingWorld - always excellent for any species due to advanced technology
        compatibility = 'A'
      } else if (Number(data.type) === 9) {
        // Destroyed planets - poor habitability, but might have salvage value
        compatibility = 'D'
      } else if (sizeCompatible && typeCompatible) {
        compatibility = 'A'
      } else if (sizeInCompatible && typeInCompatible) {
        compatibility = 'E'
      } else if ((sizeCompatible || typeCompatible) && !sizeInCompatible && !typeInCompatible) {
        compatibility = 'B'
      } else if ((sizeInCompatible || typeInCompatible) && !sizeCompatible && !typeCompatible) {
        compatibility = 'D'
      }

      // Set the sort key for the production object for planet list screen
      if (data.productionShipDesignId > 0) {
        //We are building a ship
        const design = shipDesignStore.getShipDesignById(data.productionShipDesignId)
        if (design) {
          data.productionObjectSortKey = 'aaaa:' + design.name + ':' + data.productionProgress.toString().padStart(10, '0')
        } else {
          // Design not loaded yet, use fallback
          data.productionObjectSortKey = 'aaaa:loading:' + data.productionProgress.toString().padStart(10, '0')
        }
      } else if (data.productionObjectId > 0) {
        //We are building a building
        const building = assetStore.findProductionObjectById(data.productionObjectId)
        if (building) {
          data.productionObjectSortKey =
            'Planet:' + building.productionObjectName + ':' + data.productionProgress.toString().padStart(10, '0')
        } else {
          // Building not loaded yet, use fallback
          data.productionObjectSortKey = 'Planet:loading:' + data.productionProgress.toString().padStart(10, '0')
        }
      } else {
        data.productionObjectSortKey = 'aaaaaa'
      }

      data.compatibility = compatibility

      this.planets.set(id, data)
    },
    deletePlanetById(id: number) {
      if (this.planets.has(id)) {
        this.planets.delete(id)
      }
    },
    getUnitById(id: number): PlayerPlanet | undefined {
      const planet = this.planets.get(id)
      if (planet !== undefined) {
        return planet
      } else {
        logger.warn('Planet not found: ' + id)
      }
    },
    getStarsWithNonRingworldPlanets() {
      const starsMap = new Map<number, {
        starId: number
        name: string
        type: string
        location: { x: number; y: number }
        nonRingworldCount: number
        totalPlanetCount: number
        totalConversionProgress: number
        maxConversionProgress: number
        planetsConverting: number
        planetIds: number[]
        totalPopulation: number
        homeWorldTypeCount: number
      }>()

      // First pass: count all planets per star and sum conversion progress
      const gameStore = useGameStore()
      const playerHomeWorldType = gameStore.player?.homeWorldPlanetType
      
      const starPlanetData = new Map<number, { 
        total: number; 
        nonRingworld: number; 
        conversionProgress: number;
        planetsConverting: number;
        planetIds: number[];
        homeWorldTypeCount: number;
        hasRingworld: boolean;
        totalPopulation: number;
      }>()
      
      for (const planet of this.planets.values()) {
        const data = starPlanetData.get(planet.starId) || { 
          total: 0, 
          nonRingworld: 0, 
          conversionProgress: 0,
          planetsConverting: 0,
          planetIds: [],
          totalPopulation: 0,
          homeWorldTypeCount: 0,
          hasRingworld: false
        }
        data.total++
        data.totalPopulation += planet.population || 0
        
        // Count planets of player's home world type
        if (Number(planet.type) === playerHomeWorldType) {
          data.homeWorldTypeCount++
        }
        
        // Check if this star has any ringworld planets (type 8 is ringworld)
        if (Number(planet.type) === 8) {
          data.hasRingworld = true
        } else {
          // Count non-ringworld planets
          data.nonRingworld++
          data.planetIds.push(planet.id)
          // Add conversion progress for non-ringworld planets
          data.conversionProgress += planet.ringworldConversionProgress || 0
          // Count planets that have conversion enabled
          if (planet.ringworldConversionEnabled > 0) {
            data.planetsConverting++
          }
        }
        
        starPlanetData.set(planet.starId, data)
      }

      // Second pass: build star data for stars with non-ringworld planets
      for (const planet of this.planets.values()) {
        const data = starPlanetData.get(planet.starId)!
        
        // Only include stars that have at least one non-ringworld planet AND no existing ringworlds
        if (data.nonRingworld > 0 && !data.hasRingworld && !starsMap.has(planet.starId)) {
          starsMap.set(planet.starId, {
            starId: planet.starId,
            name: planet.starName,
            type: 'Star', // Generic type since we don't have star type classification readily available
            location: { x: planet.xPos, y: planet.yPos },
            nonRingworldCount: data.nonRingworld,
            totalPlanetCount: data.total,
            totalConversionProgress: data.conversionProgress,
            maxConversionProgress: 15, // Always 15 regardless of number of planets
            planetsConverting: data.planetsConverting,
            planetIds: data.planetIds,
            totalPopulation: data.totalPopulation,
            homeWorldTypeCount: data.homeWorldTypeCount
          })
        }
      }

      return Array.from(starsMap.values())
    }
  }
})
